import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Link, Rule, ui } from '../ui';
import { formatMoney } from '@goodbuild/rules/format';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Loading, Notice, Screen, Table } from './shared';

/** See `standingFor` in `apps/api/src/lib/roles`. */
type RoleStanding = 'none' | 'applied' | 'active' | 'suspended';

interface Me {
  standing: { creator: RoleStanding };
}

interface BoardResponse {
  creator: { handle: string; displayName: string; level: number };
  /**
   * `BoardListing` from `apps/api/src/lib/creator-board`, over the wire.
   *
   * Named exactly as core names them. Two fields here were not: `modelledEarningsMinor`
   * for `earningsPerSaleMinor`, and `scheduledChange` for `pendingRateChange`.
   * Neither exists on the response, so the per-sale cell was formatting
   * `undefined` and the change cell was permanently a dash — a table that had
   * been quietly wrong since it was written, because a wider type on the client
   * of a JSON response is never a compile error.
   */
  listings: {
    productId: string;
    slug: string;
    name: string;
    developerName: string;
    creatorRateBps: number;
    currency: string;
    earningsPerSaleMinor: number;
    earningsAreMeasured: boolean;
  }[];
}

interface CodeResponse {
  code: {
    code: string;
    discountBps: number;
    enrolled: boolean;
    providerStatus: string | null;
    /** Who will settle the commission, by name. Null when it is GOODBUILD. */
    settledBy: string | null;
    claimedAt: string;
  } | null;
}

/**
 * Section 3. The opportunity board.
 *
 * Approved creators only — the rates here are commercially sensitive to
 * developers, and an applied-but-not-approved creator sees nothing. The API is
 * what enforces that and 403s; what this page adds is the way *out* of the
 * state, because the rail beside it is empty until somebody is approved (#90)
 * and a refusal with no door in it is where a creator's first visit ended.
 *
 * **One rate, on every title (#129).** There used to be a column for the
 * scheduled change beside it, because section 5.2 let a studio lower its own
 * rate on fourteen days' notice and notice nobody can see is not notice. No
 * title has a rate of its own to lower, so the column went with the machinery
 * behind it. The rate stays on the table rather than being stated once above
 * it: it is what the per-sale figure beside it is computed from, and the two
 * belong in the same row.
 *
 * The code panel sits above the rates because of what the two are for. The
 * table is a thing to browse; the code is a thing to *do*, once, and a creator
 * who has not claimed one has no way to earn from anything below it.
 */
export function BoardRoute(): React.ReactElement {
  /*
   * Whether there is a board to ask for, before asking for it (#90).
   *
   * `/me/board` refuses anybody unapproved with `The board is for approved
   * creators.`, which is a true sentence and a dead end: it names a state
   * without saying how to leave it, and somebody who arrived here from the
   * creators host — where the root redirects to `/board` — has never been
   * offered the application at all. The rail beside this page is empty for the
   * same reason, so the page has to carry the way in itself.
   *
   * The standing rather than `me.creator`, which is null until approval and so
   * cannot tell an applicant from a stranger. An applicant is waiting on us and
   * is told so; a stranger is invited to apply.
   */
  const me = useApi<Me>(['me'], '/me');
  const standing = me.data?.standing.creator;

  const query = useApi<BoardResponse>(['board'], '/me/board', { enabled: standing === 'active' });

  if (me.isPending || me.error) {
    return (
      <Screen title="Board" query={me}>
        {null}
      </Screen>
    );
  }

  if (standing !== 'active') {
    return (
      <Screen title="Board" query={{ isPending: false, error: null }}>
        <Waiting standing={standing} />
      </Screen>
    );
  }

  return (
    <Screen title="Board" aside="What each game pays you" query={query}>
      <YourCode />
      <Rule />
      {query.data && query.data.listings.length === 0 ? (
        <Empty>No games are open to creators yet.</Empty>
      ) : (
        <Table head={['Game', 'Made by', 'Rate', 'Per sale', 'Based on']}>
          {query.data?.listings.map((listing) => (
            <tr key={listing.productId}>
              <td>
                <Link href={`/g/${listing.slug}`}>{listing.name}</Link>
              </td>
              <td>{listing.developerName}</td>
              <td className={ui.numeric ?? ''}>{(listing.creatorRateBps / 100).toFixed(2)}%</td>
              <td className={ui.numeric ?? ''}>
                {formatMoney(listing.currency as never, listing.earningsPerSaleMinor)}
              </td>
              {/*
                Whether the figure beside it is measured or modelled, which is
                the column the scheduled rate change used to occupy. Section 3
                requires every number here to say which it is, and this is the
                only place a creator can find out — the card said so, the table
                did not.
              */}
              <td>{listing.earningsAreMeasured ? 'Real sales' : 'An estimate'}</td>
            </tr>
          ))}
        </Table>
      )}
    </Screen>
  );
}

/**
 * What the board says to somebody it is not open to yet.
 *
 * Three states and three different things to do about them, which is why this
 * is a panel rather than the one sentence the API refuses with. Section 3 is
 * the reason there is a wait at all: every rate on the board is commercially
 * sensitive to the studio that set it, so somebody decides before it is shown.
 *
 * The panel rather than `Empty`, for the reason the portal's own welcome gives:
 * an empty state is an invitation to act, and when the invitation is the only
 * thing on the page it gets the panel and the button.
 *
 * `undefined` reads as `none` by falling through the two named branches rather
 * than being defaulted, because it cannot arrive: the caller has already
 * returned for a `/me` that has not answered.
 */
function Waiting({ standing }: { standing: RoleStanding | undefined }): React.ReactElement {
  return (
    <div className={ui.panel ?? ''}>
      <div className={ui.stack ?? ''}>
        {standing === 'applied' ? (
          <>
            <p className="gb-eyebrow">Application received</p>
            <h2 className={ui.sectionTitle ?? ''}>Somebody is reading it</h2>
            <p className="gb-body">
              We read every one by hand. Your name is saved while we do, and the board opens here
              the moment we decide. There is nothing else to send.
            </p>
            <div>
              <Link href="/onboarding/creator" className={ui.buttonSecondary ?? ''}>
                Connect a payout account
              </Link>
            </div>
          </>
        ) : standing === 'suspended' ? (
          <>
            <p className="gb-eyebrow">Suspended</p>
            <h2 className={ui.sectionTitle ?? ''}>The board is closed on this account</h2>
            <p className="gb-body">
              What you have earned is safe. Write to us and a person will look.
            </p>
          </>
        ) : (
          <>
            <p className="gb-eyebrow">Not a creator yet</p>
            <h2 className={ui.sectionTitle ?? ''}>The board is for approved creators</h2>
            <p className="gb-body">
              It shows every game open to creators and what each one pays. Those rates are not for
              everybody to see, so a person reads every application first. Applying takes a few
              minutes and saves your name straight away.
            </p>
            <div>
              <Link href="/onboarding/creator" className={ui.button ?? ''}>
                Apply as a creator
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The creator's own code (#86).
 *
 * **Claim once, and there is no rename**, which is the only thing about this
 * panel that needs explaining to the person using it. A code goes into video
 * descriptions the day it is chosen, and it is registered with the merchant of
 * record as a discount bearing that exact string — so changing it would break
 * sales still arriving under the old one. The form says so before the button
 * rather than after, because "you cannot undo this" discovered afterwards is
 * the worst possible moment to learn it.
 *
 * What it deliberately does not do is validate the code here beyond what the
 * input can. The shape rule lives in the database as a CHECK, is restated in
 * `lib/creators/codes.ts` as a sentence, and arrives here as
 * `ApiError.message` — and a third copy in a browser is the copy that drifts.
 * A duplicate cannot be caught client-side at all: it is decided by a unique
 * index at the moment of pressing, and by a merchant of record that may have
 * its own opinion. The refusal is shown verbatim.
 */
function YourCode(): React.ReactElement {
  const key = ['creator', 'code'] as const;
  const query = useApi<CodeResponse>(key, '/me/code');
  const call = useApiMutation();
  const cache = useQueryClient();
  const [wanted, setWanted] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const code = query.data?.code ?? null;

  async function claim(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await call('/me/code', { method: 'POST', body: JSON.stringify({ code: wanted.trim() }) });
      await cache.invalidateQueries({ queryKey: key });
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  /*
   * The mark rather than nothing (#100).
   *
   * This panel sits under the listings, which have already settled by the time
   * `/me/code` lands — so an empty fragment meant a section of the page
   * materialised out of blank space a beat after everything above it had
   * stopped moving. Labelled, because it is the second request on this screen
   * and the reader should not have to guess which one is still out.
   */
  if (query.isPending) return <Loading label="Your code" />;

  if (code) {
    return (
      <>
        <h2 className={ui.sectionTitle ?? ''}>Your code</h2>
        <p className="gb-body">
          Viewers who buy with <strong>{code.code}</strong> get{' '}
          {(code.discountBps / 100).toFixed(0)}% off, and the sale counts as yours.
        </p>
        {/*
          The enrolment state, and it is worth saying out loud rather than
          hiding. Between claiming a code and accepting the invitation the code
          works perfectly — the discount applies and the sale is still
          attributed — and the only thing that differs is who pays the creator.
          Somebody who has not seen the email needs to be told there is one.
        */}
        {code.enrolled || !code.settledBy ? null : (
          <Notice>
            Your code works already. Look in your email for an invitation from {code.settledBy}
            and accept it. Until you do, we pay you instead of them, which is slower.
          </Notice>
        )}
      </>
    );
  }

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Your code</h2>
      <p className="gb-body">
        Pick the code your viewers will type when they pay. It gives them 10% off and the sale
        counts as yours. <strong>You cannot change it later</strong> — it goes into your video
        descriptions and to the payment company.
      </p>
      <form onSubmit={(event) => void claim(event)}>
        <Field label="Code">
          <input
            value={wanted}
            onChange={(event) => setWanted(event.target.value)}
            minLength={3}
            maxLength={32}
            // The same set as `creator_code_shape` in the schema. It is a
            // convenience rather than the rule — the CHECK is the rule, and the
            // server's sentence is what somebody is actually shown.
            pattern="[A-Za-z0-9_\-]{3,32}"
            placeholder="yourname"
            required
          />
        </Field>
        {message ? <Notice alert>{message}</Notice> : null}
        <button type="submit" className={ui.button ?? ''} disabled={busy || wanted.trim() === ''}>
          {busy ? 'Claiming…' : 'Claim this code'}
        </button>
      </form>
    </>
  );
}
